import React, { PureComponent } from "react";
import { MAX_TABS } from "../shared/mcp-chat-config";
import type { ChatTab } from "../shared/mcp-chat-types";

/** How many tabs render inline before the rest collapse into the overflow menu. */
const VISIBLE_TAB_LIMIT = 3;

interface McpChatTabBarProps {
  tabs: ChatTab[];
  activeTabId: string;
  onSwitchTab: (tabId: string) => void;
  onCloseTab: (tabId: string) => void;
  onAddTab: () => void;
  onRenameTab: (tabId: string, newName: string) => void;
}

interface McpChatTabBarState {
  editingTabId: string | null;
  editingTabName: string;
  overflowOpen: boolean;
}

interface TabSplit {
  visible: ChatTab[];
  overflow: ChatTab[];
}

class McpChatTabBar extends PureComponent<McpChatTabBarProps, McpChatTabBarState> {
  state: McpChatTabBarState = {
    editingTabId: null,
    editingTabName: "",
    overflowOpen: false
  };

  private overflowRef = React.createRef<HTMLDivElement>();

  componentDidMount(): void {
    document.addEventListener("mousedown", this.handleDocMouseDown);
  }

  componentWillUnmount(): void {
    document.removeEventListener("mousedown", this.handleDocMouseDown);
  }

  private handleDocMouseDown = (e: MouseEvent): void => {
    if (!this.state.overflowOpen) return;
    if (this.overflowRef.current && !this.overflowRef.current.contains(e.target as Node)) {
      this.setState({ overflowOpen: false });
    }
  };

  /**
   * Split tabs into the inline-visible set (≤ VISIBLE_TAB_LIMIT) and the
   * overflow set. The active tab is always kept visible — if it would land in
   * the overflow, it takes the last visible slot.
   */
  private splitTabs(tabs: ChatTab[], activeTabId: string): TabSplit {
    if (tabs.length <= VISIBLE_TAB_LIMIT) {
      return { visible: tabs, overflow: [] };
    }
    const activeIndex: number = tabs.findIndex((t: ChatTab): boolean => t.id === activeTabId);
    if (activeIndex < VISIBLE_TAB_LIMIT) {
      return { visible: tabs.slice(0, VISIBLE_TAB_LIMIT), overflow: tabs.slice(VISIBLE_TAB_LIMIT) };
    }
    // Active is in the overflow — surface it in the last visible slot.
    const activeTab: ChatTab = tabs[activeIndex];
    const visible: ChatTab[] = [...tabs.slice(0, VISIBLE_TAB_LIMIT - 1), activeTab];
    const overflow: ChatTab[] = tabs.filter((t: ChatTab): boolean => !visible.includes(t));
    return { visible, overflow };
  }

  handleTabDoubleClick = (tabId: string) => {
    const tab = this.props.tabs.find((t) => t.id === tabId);
    if (!tab) return;
    this.setState({ editingTabId: tabId, editingTabName: tab.name });
  };

  handleTabNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ editingTabName: e.target.value });
  };

  handleTabNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      this.commitTabRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      this.cancelTabRename();
    }
  };

  handleTabNameBlur = () => {
    this.commitTabRename();
  };

  commitTabRename = () => {
    const { editingTabId, editingTabName } = this.state;
    if (!editingTabId) return;

    const trimmed = editingTabName.trim();
    if (trimmed) {
      const tab = this.props.tabs.find((t) => t.id === editingTabId);
      if (tab && trimmed !== tab.name) {
        this.props.onRenameTab(editingTabId, trimmed);
      }
    }
    this.setState({ editingTabId: null, editingTabName: "" });
  };

  cancelTabRename = () => {
    this.setState({ editingTabId: null, editingTabName: "" });
  };

  private handleOverflowSelect = (tabId: string): void => {
    this.setState({ overflowOpen: false });
    this.props.onSwitchTab(tabId);
  };

  /** Expose editing state for parent (e.g. Escape key guard) */
  isEditing = (): boolean => this.state.editingTabId !== null;

  private renderTab(tab: ChatTab): React.ReactNode {
    const { activeTabId, onSwitchTab, onCloseTab, tabs } = this.props;
    const { editingTabId, editingTabName } = this.state;
    const isActive = tab.id === activeTabId;
    const isEditing = editingTabId === tab.id;
    const tabClass = ["mcp-chat__tab", isActive && "mcp-chat__tab--active", tab.isLoading && "mcp-chat__tab--loading"]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        key={tab.id}
        className={tabClass}
        onClick={() => !isEditing && onSwitchTab(tab.id)}
        onDoubleClick={() => this.handleTabDoubleClick(tab.id)}
        title={tab.name}
        role="tab"
        aria-selected={isActive}
        tabIndex={isActive ? 0 : -1}
      >
        {tab.isLoading && <span className="mcp-chat__tab-pulse" />}
        {isEditing ? (
          <input
            className="mcp-chat__tab-rename-input"
            value={editingTabName}
            onChange={this.handleTabNameChange}
            onKeyDown={this.handleTabNameKeyDown}
            onBlur={this.handleTabNameBlur}
            onDoubleClick={(e) => e.stopPropagation()}
            autoFocus
            onFocus={(e) => e.currentTarget.select()}
            maxLength={50}
            aria-label="Rename tab"
          />
        ) : (
          <span className="mcp-chat__tab-name">{tab.name}</span>
        )}
        {tabs.length > 1 && !isEditing && (
          <span
            className="mcp-chat__tab-close"
            onClick={(e) => {
              e.stopPropagation();
              onCloseTab(tab.id);
            }}
            role="button"
            aria-label={`Close ${tab.name}`}
          >
            ×
          </span>
        )}
      </div>
    );
  }

  render() {
    const { tabs, activeTabId, onAddTab, onCloseTab } = this.props;
    const { overflowOpen } = this.state;
    const { visible, overflow } = this.splitTabs(tabs, activeTabId);

    return (
      <div className="mcp-chat__tab-bar" role="tablist" aria-label="Chat tabs">
        <div className="mcp-chat__tab-list">{visible.map((tab: ChatTab) => this.renderTab(tab))}</div>
        {overflow.length > 0 && (
          <div className="mcp-chat__tab-overflow" ref={this.overflowRef}>
            <button
              className={`mcp-chat__tab-overflow-trigger${overflowOpen ? " mcp-chat__tab-overflow-trigger--open" : ""}`}
              onClick={() => this.setState((prev: McpChatTabBarState) => ({ overflowOpen: !prev.overflowOpen }))}
              aria-haspopup="menu"
              aria-expanded={overflowOpen}
              aria-label={`${overflow.length} more chats`}
            >
              {overflow.length} more
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {overflowOpen && (
              <div className="mcp-chat__tab-overflow-menu" role="menu">
                {overflow.map((tab: ChatTab) => (
                  <div
                    key={tab.id}
                    className="mcp-chat__tab-overflow-item"
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => this.handleOverflowSelect(tab.id)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === "Enter" || e.key === " ") this.handleOverflowSelect(tab.id);
                    }}
                    title={tab.name}
                  >
                    {tab.isLoading && <span className="mcp-chat__tab-pulse" />}
                    <span className="mcp-chat__tab-overflow-item-name">{tab.name}</span>
                    {tabs.length > 1 && (
                      <span
                        className="mcp-chat__tab-close"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCloseTab(tab.id);
                        }}
                        role="button"
                        aria-label={`Close ${tab.name}`}
                      >
                        ×
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {tabs.length < MAX_TABS && (
          <button className="mcp-chat__tab-add" onClick={onAddTab} title="New chat" aria-label="New chat tab">
            +
          </button>
        )}
      </div>
    );
  }
}

export default McpChatTabBar;
